import { createApp } from "vue"

// Tracks all mounted Vue apps so they can be unmounted before Turbo caches the page.
const mountedApps = new Map()

/**
 * Mount Vue components onto every `[data-vue="name"]` element in the document.
 *
 * Props are sourced from individual `data-*` attributes on the element.
 * Each attribute is camelCased automatically by the browser's `dataset` API,
 * e.g. `data-form-prefix` becomes `el.dataset.formPrefix`.
 *
 * @param {Record<string, Component>} componentMap  e.g. { "steps-editor": StepsEditor }
 */
export function mountComponents(componentMap) {
  for (const [name, Component] of Object.entries(componentMap)) {
    document.querySelectorAll(`[data-vue="${name}"]`).forEach((el) => {
      if (mountedApps.has(el)) return

      const app = createApp(Component, { ...el.dataset })
      app.mount(el)
      mountedApps.set(el, app)
    })
  }
}

/**
 * Unmount all Vue apps. Called on `turbo:before-cache` so Turbo's snapshot of
 * the page doesn't include live Vue instances, which would break back-navigation.
 */
export function unmountComponents() {
  mountedApps.forEach((app) => {
    try {
      app.unmount()
    } catch (_) {
      // ignore errors for already-removed elements
    }
  })
  mountedApps.clear()
}
